import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, ShieldCheckIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { getConfig } from '../services/configManager';
import { useWatchdog } from '../hooks/useWatchdog';
import { ExistingCard, CardPaymentModalResult } from '../types/api';
import WindcaveProvider from './providers/WindcaveProvider';
import DatacapProvider from './providers/DatacapProvider';
import ExistingCardList from './ExistingCardList';

type ModalView = 'loading' | 'cardEntry' | 'existingCards' | 'success' | 'failed';

export interface CardPaymentModalState {
  isOpen: boolean;
  title: string;
  existingCards?: ExistingCard[];
}

interface CardPaymentModalProps {
  state: CardPaymentModalState;
  onResolve: (result: CardPaymentModalResult) => void;
}

const CardPaymentModal: React.FC<CardPaymentModalProps> = ({ state, onResolve }) => {
  const { isOpen, title, existingCards } = state;
  const [view, setView] = useState<ModalView>('loading');
  const [sid, setSid] = useState('');
  const [payloadBase64, setPayloadBase64] = useState('');
  const [provider, setProvider] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const watchdog = useWatchdog();
  const resolvedRef = useRef(false);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resolve = useCallback((result: CardPaymentModalResult) => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    watchdog.stop();
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    onResolve(result);
  }, [onResolve, watchdog]);

  const initSession = useCallback(async () => {
    setView('loading');
    setErrorMessage('');

    try {
      const response = await apiService.initHppSession((getConfig() as any).payment);
      setSid(response.sid);
      setPayloadBase64(response.payloadBase64 ?? '');
      setProvider(response.provider ?? '');

      watchdog.start(response.sid, {
        onSuccess: () => {
          setView('success');
          autoCloseTimerRef.current = setTimeout(() => {
            resolve({ status: 'success', selected: 'newCard', newCard: { sid: response.sid } });
          }, 2000);
        },
        onFailed: () => {
          setErrorMessage('Card validation failed. Please try again.');
          setView('failed');
        },
        onCanceled: () => {
          resolve({ status: 'cancelled' });
        },
      });

      const hasCards = existingCards && existingCards.length > 0;
      setView(hasCards ? 'existingCards' : 'cardEntry');
    } catch (error) {
      console.error('Failed to initialize payment session:', error);
      setErrorMessage('Failed to initialize payment. Please try again.');
      setView('failed');
    }
  }, [existingCards, watchdog, resolve]);

  useEffect(() => {
    if (isOpen) {
      resolvedRef.current = false;
      initSession();
    }
    return () => {
      watchdog.stop();
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    resolve({ status: 'cancelled' });
  };

  const handleExistingCardSelect = (token: string) => {
    resolve({ status: 'success', selected: 'existingCard', existingCard: { token } });
  };

  const handleRetry = () => {
    initSession();
  };

  const hasExistingCards = existingCards && existingCards.length > 0;

  const renderProvider = () => {
    if (!sid || !payloadBase64) return null;

    switch (provider) {
      case 'windcave':
        return <WindcaveProvider sid={sid} payloadBase64={payloadBase64} />;
      case 'datacap':
        return <DatacapProvider sid={sid} payloadBase64={payloadBase64} />;
      default:
        return <div className="text-red-600 text-center py-4">Unsupported payment provider.</div>;
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'loading':
        return (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-btn" />
            <span className="ml-3 text-text-disabled">Loading payment form...</span>
          </div>
        );

      case 'existingCards':
        return (
          <ExistingCardList
            cards={existingCards!}
            onSelect={handleExistingCardSelect}
            onUseNewCard={() => setView('cardEntry')}
          />
        );

      case 'cardEntry':
        return (
          <div className="flex flex-col gap-4">
            {renderProvider()}

            {hasExistingCards && (
              <button
                type="button"
                onClick={() => setView('existingCards')}
                className="text-primary-btn text-sm font-semibold hover:underline self-center"
              >
                Use existing card
              </button>
            )}
          </div>
        );

      case 'success':
        return (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircleIcon className="h-16 w-16 text-green-500" />
            <p className="text-black text-md font-semibold">Card registration successful</p>
          </div>
        );

      case 'failed':
        return (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500" />
            <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Rejected</p>
           
            {/* <p className="text-black text-md text-center">{errorMessage}</p> */}
            
            <div className="w-full bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl px-4 py-3.5 flex flex-col gap-2.5 text-left">
              <div className="flex items-start gap-2.5">
                <InformationCircleIcon className="h-[18px] w-[18px] text-text-disabled flex-shrink-0 mt-px" />
                <span className="text-[13px] text-text-disabled leading-[18px]">Please double-check that you entered a valid card number, or try again later.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <InformationCircleIcon className="h-[18px] w-[18px] text-text-disabled flex-shrink-0 mt-px" />
                <span className="text-[13px] text-text-disabled leading-[18px]">Your card was not charged.</span>
              </div>
            </div>

            <button onClick={handleRetry} className="btn-primary">
              Try Again
            </button>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-black font-semibold text-lg">
              {title}
            </Dialog.Title>
            <button onClick={handleClose} className="text-text-disabled hover:text-black">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {renderContent()}
          </div>

          {provider && view !== 'loading' && view !== 'success' && view !== 'failed' && (
            <div className="flex items-center justify-center gap-1.5 mt-4 pt-3 border-t border-border">
              <ShieldCheckIcon className="h-4 w-4 text-text-disabled" />
              <span className="text-text-disabled text-sm">
                Secured by {provider.charAt(0).toUpperCase() + provider.slice(1)}
              </span>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default CardPaymentModal;
